import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import bgImage from '../assets/images/new_bgs.png';
import logoImage from '../assets/images/payPlus/payPlusNoBackground.png';
import logoImageWithTitle from '../assets/images/payPlus/payPlusWithTitle.png';
import { useAuth } from '../utils/AuthProvider';
import InputText from '../components/InputText';
import toastService from '../utils/ToastService';
import { useEffect } from 'react';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    localStorage.setItem('visited_count', '1');
  }, []);

  const formik = useFormik({
    initialValues: { username: '', password: '' },
    validate: data => {
      const errors = {};
      if (!data.username) errors.username = t('usernameRequired');
      if (!data.password) errors.password = t('passwordRequired');
      return errors;
    },
    onSubmit: async data => {
      try {
        const success = await login(data.username, data.password);
        if (success) {
          navigate('/homepage');
        } else {
          toastService.warn(
            'Abonelik süreniz dolduğu için sisteme giriş yapamıyorsunuz. Abonelik ödeme sayfasına yönlendiriliyorsunuz.',
            'Abonelik Süresi Doldu'
          );

          setTimeout(() => {
            navigate('/pricing');
          }, 3000);
        }
      } catch (err) {
        // AuthProvider'da zaten detaylı hata mesajları gösteriliyor
        // Burada sadece beklenmeyen hatalar için genel mesaj gösterelim
        console.error('Login error:', err);
      }
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Form validasyonu
    if (!formik.values.username.trim()) {
      toastService.error('Kullanıcı adı boş olamaz.', 'Zorunlu Alan');
      return;
    }

    if (!formik.values.password.trim()) {
      toastService.error('Şifre boş olamaz.', 'Zorunlu Alan');
      return;
    }

    formik.handleSubmit(e);
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="relative flex gap-0 lg:gap-10 p-3 sm:p-5 lg:p-10 h-full">
        {/* Sol kısım - sadece büyük ekranlarda görünür */}
        <div className="hidden lg:flex h-[90vh] w-[60vw] bg-white bg-opacity-50 justify-center items-center rounded-3xl">
          <div className="h-[90%] w-[90%] flex justify-start items-center bg-white bg-opacity-80 p-2 rounded-3xl relative shadow-2xl shadow-gray-500">
            <img className='dark:filter dark:grayscale-[70%]'
              src={logoImageWithTitle}
              alt="Logo"
            />
          </div>
        </div>

        {/* Sağ kısım - responsive form container */}
        <div className="h-[90vh] w-full lg:w-[40vw] bg-white bg-opacity-50 flex justify-center items-center rounded-3xl">
          <div className="bg-white bg-opacity-90 rounded-3xl shadow-2xl shadow-gray-500 w-full max-w-lg min-w-[280px] mx-4">
            <div className="p-4 sm:p-6 lg:p-8">
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="h-[8vh] sm:h-[10vh] lg:h-[12vh] w-auto max-w-[120px] object-contain dark:filter dark:grayscale-[70%]"
                />
              </div>

              {/* Başlık */}
              <div className="text-center flex flex-col items-center gap-y-3 sm:gap-y-4 mb-8">
                <h1 className="font-firaSans font-bold text-xl sm:text-2xl lg:text-3xl text-logo-darkest">
                  {t('login')}
                </h1>
                <p className="text-xs sm:text-sm text-logo-dark px-2">{t('youcanlogin')}</p>
              </div>

              {/* Form */}
              <form onSubmit={handleFormSubmit} className="flex flex-col gap-y-6">
                <InputText
                  id="username"
                  label={t('username')}
                  name="username"
                  type="text"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("username", "")}
                  error={formik.errors.username}
                  touched={formik.touched.username}
                  required
                />
                <InputText
                  id="password"
                  showTemplate={false}
                  showFooter={false}
                  label={t('password')}
                  name="password"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  clearFunction={() => formik.setFieldValue("password", "")}
                  error={formik.errors.password}
                  touched={formik.touched.password}
                  required
                />
                <Button
                  type="submit"
                  label={t('login')}
                  className="bg-logo-mid hover:bg-logo-dark dark:bg-gray-500 dark:hover:bg-gray-600 py-3 sm:py-4 lg:py-5 text-white font-medium rounded-lg shadow-lg text-sm sm:text-base"
                  loading={formik.isSubmitting}
                />
              </form>

              {/* Alt linkler */}
              <div className='flex flex-col gap-4 mt-6'>
                <p
                  onClick={() => navigate('/new-password')}
                  className="text-center text-logo-dark font-semibold hover:underline hover:text-logo-darkest text-xs sm:text-sm lg:text-base cursor-pointer px-2"
                >
                  {t('forgetPassword')}
                </p>
                <p
                  onClick={() => navigate('/register')}
                  className="text-center text-logo-dark font-semibold hover:underline hover:text-logo-darkest text-xs sm:text-sm lg:text-base cursor-pointer px-2"
                >
                  {t('register')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;